import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { DeleteOutline, ExpandMore, PrintOutlined } from "@mui/icons-material";
import { ProductCreator, ProductType, Supplier } from "@/interfaces";
import { baseUrl } from "@/consts";
import {
  isNonEmptyString,
  formatName,
  isFunction,
  isNumber,
  isStringEqualCI,
} from "@/utils";
import { colors, sxFont_12_400 } from "@/theme";
import CrudGrid from "../CrudGrid";
import { CrudGridProps, CrudGridRef } from "../CrudGrid/interface";

export type ProductAccordionProps = CrudGridProps & {
  creator?: ProductCreator;
  search?: string;
  suppliers?: Supplier[];
  productTypes?: ProductType[];
  onProductList?: (
    creator?: ProductCreator,
    filter?: {
      supplierId?: string;
      productTypeId?: string;
      /** When non-empty, PDF should include only these product IDs (server must support). */
      productIds?: number[];
    }
  ) => void;
};

export interface ProductAccordionRef {
  reloadData: (createdBy?: string) => void;
}

const ProductAccordion = forwardRef<ProductAccordionRef, ProductAccordionProps>(
  ({ creator, onProductList, suppliers, productTypes, ...props }, ref) => {
    const childRef = useRef<CrudGridRef | null>(null);
    const [totalRowCount, setTotalRowCount] = useState<number | undefined>(
      undefined
    );
    const [printSupplierId, setPrintSupplierId] = useState<string>("");
    const [printProductTypeId, setPrintProductTypeId] = useState<string>("");

    const reloadData = (createdBy?: string) => {
      if (
        !isNonEmptyString(createdBy) ||
        !isStringEqualCI(creator?.createdBy, createdBy)
      ) {
        return;
      }

      childRef?.current?.reloadData?.();
    };

    const deleteSelectedRows = () => {
      childRef?.current?.deleteSelectedRows?.();
    };

    useEffect(() => {
      childRef?.current?.reloadData?.();
    }, [printSupplierId, printProductTypeId]);

    useImperativeHandle(ref, () => ({
      reloadData: reloadData,
      deleteSelectedRows: deleteSelectedRows,
    }));

    const onHeaderLabelProductListClick = (
      e: React.MouseEvent<HTMLSpanElement>
    ) => {
      e.stopPropagation();

      if (isFunction(onProductList)) {
        const rawIds = childRef.current?.getSelectedRowIds?.() ?? [];
        const productIds = rawIds
          .map((id) => (typeof id === "number" ? id : Number(id)))
          .filter((id) => isNumber(id) && !Number.isNaN(id));

        onProductList?.(creator, {
          supplierId: isNonEmptyString(printSupplierId)
            ? printSupplierId
            : undefined,
          productTypeId: isNonEmptyString(printProductTypeId)
            ? printProductTypeId
            : undefined,
          ...(productIds.length > 0 ? { productIds } : {}),
        });
      }
    };

    const onHeaderLabelDeleteSelectedRowsClick = (
      e: React.MouseEvent<HTMLSpanElement>
    ) => {
      e.stopPropagation();

      deleteSelectedRows();
    };

    if (!creator || !isNonEmptyString(creator.createdBy)) {
      return undefined;
    }

    const displayLabel =
      formatName(creator.creatorFirstName, creator.creatorLastName) ||
      creator.createdBy;

    return (
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          key={creator.createdBy}
          aria-controls={`productAccordion-${creator.createdBy}-content`}
          id={`productAccordion-${creator.createdBy}-header`}
          sx={{
            "& .MuiAccordionSummary-content": {
              justifyContent: "space-between",
            },
          }}
        >
          <Typography component="span" sx={{ ...sxFont_12_400 }}>
            {displayLabel}
          </Typography>
          <Box
            sx={{
              display: "inline-flex",
              columnGap: "10px",
              alignItems: "center",
            }}
          >
            <FormControl
              size="small"
              sx={{ minWidth: 150 }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <InputLabel id={`print-supplier-${creator.createdBy}`}>
                Supplier
              </InputLabel>
              <Select
                labelId={`print-supplier-${creator.createdBy}`}
                value={printSupplierId}
                label="Supplier"
                onChange={({ target: { value } }) =>
                  setPrintSupplierId(value as string)
                }
              >
                <MenuItem value="">
                  <em>All suppliers</em>
                </MenuItem>
                {(suppliers ?? []).map((s) => (
                  <MenuItem
                    key={s.supplierId}
                    value={s.supplierId != null ? String(s.supplierId) : ""}
                  >
                    {s.supplierName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 150 }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <InputLabel id={`print-product-type-${creator.createdBy}`}>
                Product type
              </InputLabel>
              <Select
                labelId={`print-product-type-${creator.createdBy}`}
                value={printProductTypeId}
                label="Product type"
                onChange={({ target: { value } }) =>
                  setPrintProductTypeId(value as string)
                }
              >
                <MenuItem value="">
                  <em>All types</em>
                </MenuItem>
                {(productTypes ?? []).map((pt) => (
                  <MenuItem
                    key={pt.productTypeId}
                    value={
                      pt.productTypeId != null ? String(pt.productTypeId) : ""
                    }
                  >
                    {pt.productTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography
              component="span"
              title="Print to PDF"
              onClick={onHeaderLabelProductListClick}
              sx={{
                ...sxFont_12_400,
                color: colors.primaryOzone,
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              {"Print to PDF"}
              <PrintOutlined sx={{ ml: "5px" }} />
            </Typography>
            <Typography
              component="span"
              title="Delete selected rows"
              onClick={onHeaderLabelDeleteSelectedRowsClick}
              sx={{
                ...sxFont_12_400,
                color: colors.accent3Error,
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              {"Delete"}
              <DeleteOutline sx={{ ml: "5px" }} />
            </Typography>
            <Typography
              component="span"
              sx={{ ...sxFont_12_400, color: colors.primaryOzone }}
            >
              {`(${totalRowCount})`}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ display: "flex", flexDirection: "column" }}>
          <CrudGrid
            ref={childRef}
            baseUrl={baseUrl.PRODUCT}
            additionalURLSearchParams={{
              createdBy: creator.createdBy,
              ...(isNonEmptyString(printSupplierId) && {
                supplierId: printSupplierId,
              }),
              ...(isNonEmptyString(printProductTypeId) && {
                productTypeId: printProductTypeId,
              }),
            }}
            setTotalRowCount={setTotalRowCount}
            checkboxSelection={true}
            hideSearchBar={true}
            {...props}
          />
        </AccordionDetails>
      </Accordion>
    );
  }
);

export default ProductAccordion;
